import React, { useEffect, useState, useRef } from 'react'
import axios from 'axios'
import { LinkContainer } from 'react-router-bootstrap'
import { Table, Button, Row, Col, Form } from 'react-bootstrap'
import { useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import Message from '../components/Message'
import Loader from '../components/Loader'
import Paginate from '../components/Paginate'
import { Toast } from "../services/Toast";

import {
  listProducts,
  deleteProduct,
  createProduct,
  bulkDeleteProducts,
} from '../actions/productActions'
import { PRODUCT_CREATE_RESET } from '../constants/productConstants'
const apiUrl = process.env.REACT_APP_API_URL;

const sortOptions = [
  { label: 'Product title', field: 'name' },
  { label: 'Price', field: 'price' },
  { label: 'Inventory', field: 'quantity' },
  { label: 'Status', field: 'isActive' },
  { label: 'Brand', field: 'brand' },
]

const ProductListScreen = ({ history, match }) => {
  const location = useLocation()
  const pageNumber = match.params.pageNumber || 1
  const searchParams = new URLSearchParams(location.search)
  const keyword = searchParams.get('keyword') || ''

  const dispatch = useDispatch()

  const productList = useSelector((state) => state.productList)
  const { loading, error, products, page, pages } = productList

  const productDelete = useSelector((state) => state.productDelete)
  const {
    loading: loadingDelete,
    error: errorDelete,
    success: successDelete,
  } = productDelete

  const productBulkDelete = useSelector((state) => state.productBulkDelete)
  const {
    loading: loadingBulkDelete,
    error: errorBulkDelete,
    success: successBulkDelete,
  } = productBulkDelete

  const productCreate = useSelector((state) => state.productCreate)
  const {
    loading: loadingCreate,
    error: errorCreate,
    success: successCreate,
    product: createdProduct,
  } = productCreate

  const userLogin = useSelector((state) => state.userLogin)
  const { userInfo } = userLogin
  const [searchTerm, setSearchTerm] = useState(keyword)
  const [pageSize, setPageSize] = useState(0)
  const [selectedProducts, setSelectedProducts] = useState([])
  const [sortField, setSortField] = useState('name')
  const [sortDirection, setSortDirection] = useState('asc')
  const [showSortDropdown, setShowSortDropdown] = useState(false)
  const [showSearch, setShowSearch] = useState(!!keyword)
  const [statusFilter, setStatusFilter] = useState('all')
  const searchInputRef = useRef(null)
  const sortDropdownRef = useRef(null)

  const currentPage = Number(page) || Number(pageNumber) || 1
  const totalPages = Number(pages) || 1
  const resolvedPageSize = pageSize || products.length
  const serialOffset =
    resolvedPageSize > 0 ? (currentPage - 1) * resolvedPageSize : 0

  const filteredProducts = React.useMemo(() => {
    if (statusFilter === 'all') return products
    if (statusFilter === 'active') return products.filter((p) => p.isActive === true)
    if (statusFilter === 'draft') return products.filter((p) => !p.isActive)
    return products
  }, [products, statusFilter])

  const sortedProducts = React.useMemo(() => {
    return [...filteredProducts].sort((a, b) => {
      // Drafts always sink to the bottom, regardless of the column sort.
      const aDraft = a.isActive ? 0 : 1
      const bDraft = b.isActive ? 0 : 1
      if (aDraft !== bDraft) return aDraft - bDraft
      if (!sortField) return 0
      let valA = a[sortField]
      let valB = b[sortField]
      if (typeof valA === 'string') valA = valA.toLowerCase()
      if (typeof valB === 'string') valB = valB.toLowerCase()
      if (valA < valB) return sortDirection === 'asc' ? -1 : 1
      if (valA > valB) return sortDirection === 'asc' ? 1 : -1
      return 0
    })
  }, [filteredProducts, sortField, sortDirection])

  const allSelected = sortedProducts.length > 0 && selectedProducts.length === sortedProducts.length

  const deleteHandler = (id) => {
    if (window.confirm("This can't be undone")) {
      dispatch(deleteProduct(id))
    }
  }

  const bulkDeleteHandler = () => {
    if (selectedProducts.length === 0) return
    if (window.confirm(`Delete ${selectedProducts.length} selected product(s)? This can't be undone.`)) {
      dispatch(bulkDeleteProducts(selectedProducts))
    }
  }

  const toggleSelectProduct = (id) => {
    setSelectedProducts((prev) =>
      prev.includes(id) ? prev.filter((pid) => pid !== id) : [...prev, id]
    )
  }

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedProducts([])
    } else {
      setSelectedProducts(sortedProducts.map((p) => p._id))
    }
  }

  const duplicateHandler = (id) => {
    axios({
      method: 'post',
      headers: {
        Authorization: `Bearer ${userInfo.token}`,
      },
      url: `${apiUrl}/api/products/duplicate/${id}`,
    }).then((res) => {
      if (res.status === 200) {
        Toast({ type: 'success', message: 'Product duplicated successfully!' });
        dispatch(listProducts(keyword, pageNumber));
      }
    })
  }

  useEffect(() => {
    dispatch({ type: PRODUCT_CREATE_RESET })
    if (!userInfo || !userInfo.isAdmin) {
      history.push('/login')
      return
    }

    if (successCreate) {
      history.push(`/admin/product/${createdProduct.slug}/create`)
      return
    }

    dispatch(listProducts(keyword, pageNumber))
  }, [
    dispatch,
    history,
    userInfo,
    successCreate,
    createdProduct,
    pageNumber,
    successDelete,
    successBulkDelete,
    keyword,
  ])

  useEffect(() => {
    if (successDelete) {
      Toast({ type: 'success', message: 'Product deleted successfully!' })
    }
  }, [successDelete])

  useEffect(() => {
    if (successBulkDelete) {
      Toast({ type: 'success', message: 'Selected products deleted successfully!' })
      setSelectedProducts([])
    }
  }, [successBulkDelete])

  useEffect(() => {
    setSelectedProducts([])
  }, [products])

  const navigateToSearch = (term) => {
    const trimmed = term.trim()
    if (trimmed) {
      history.push(`/admin/productlist/1?keyword=${encodeURIComponent(trimmed)}`)
      return
    }
    history.push('/admin/productlist/1')
  }

  useEffect(() => {
    const trimmed = searchTerm.trim()
    if (trimmed === keyword) return

    const timeoutId = setTimeout(() => {
      navigateToSearch(searchTerm)
    }, 300)

    return () => clearTimeout(timeoutId)
  }, [searchTerm, keyword, history])

  useEffect(() => {
    setSearchTerm(keyword)
  }, [keyword])

  useEffect(() => {
    if (products.length === 0) return
    if (pageSize === 0 || currentPage < totalPages || products.length > pageSize) {
      setPageSize(products.length)
    }
  }, [products.length, pageSize, currentPage, totalPages])

  useEffect(() => {
    if (showSearch && searchInputRef.current) {
      searchInputRef.current.focus()
    }
  }, [showSearch])

  // Close sort dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (sortDropdownRef.current && !sortDropdownRef.current.contains(e.target)) {
        setShowSortDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleSearchCancel = () => {
    setShowSearch(false)
    if (keyword) {
      setSearchTerm('')
      history.push('/admin/productlist/1')
    }
  }

  const toolbarStyles = {
    wrapper: {
      border: '1px solid #e1e3e5',
      borderRadius: '10px',
      overflow: 'hidden',
      marginBottom: '0',
      background: '#fff',
    },
    tabBar: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '0 16px',
      borderBottom: '1px solid #e1e3e5',
      background: '#fafbfb',
    },
    tabsLeft: {
      display: 'flex',
      alignItems: 'center',
      gap: '0',
    },
    statusLabel: {
      fontSize: '13px',
      fontWeight: '500',
      color: '#6d7175',
      marginRight: '8px',
    },
    statusSelect: {
      padding: '7px 12px',
      fontSize: '14px',
      fontWeight: '500',
      color: '#202223',
      background: '#fff',
      border: '1px solid #c9cccf',
      borderRadius: '8px',
      cursor: 'pointer',
      outline: 'none',
      margin: '9px 0',
    },
    tab: (active) => ({
      padding: '12px 16px',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: active ? '600' : '400',
      color: active ? '#202223' : '#6d7175',
      borderBottom: active ? '2px solid #202223' : '2px solid transparent',
      background: 'transparent',
      border: 'none',
      borderBottomWidth: '2px',
      borderBottomStyle: 'solid',
      borderBottomColor: active ? '#202223' : 'transparent',
      transition: 'all 0.15s ease',
    }),
    iconsRight: {
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
    },
    iconBtn: {
      background: 'transparent',
      border: 'none',
      padding: '8px',
      cursor: 'pointer',
      color: '#6d7175',
      fontSize: '16px',
      borderRadius: '6px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'background 0.15s ease',
    },
    searchBar: {
      display: 'flex',
      alignItems: 'center',
      padding: '8px 16px',
      borderBottom: '1px solid #e1e3e5',
      background: '#fff',
      gap: '8px',
    },
    searchInput: {
      flex: 1,
      border: '2px solid #2c6ecb',
      borderRadius: '8px',
      padding: '8px 12px 8px 36px',
      fontSize: '14px',
      outline: 'none',
      background: '#fff',
    },
    searchInputWrapper: {
      flex: 1,
      position: 'relative',
    },
    searchIcon: {
      position: 'absolute',
      left: '12px',
      top: '50%',
      transform: 'translateY(-50%)',
      color: '#8c9196',
      fontSize: '14px',
    },
    cancelBtn: {
      background: 'transparent',
      border: 'none',
      color: '#2c6ecb',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: '500',
      padding: '8px 12px',
    },
    sortDropdown: {
      position: 'absolute',
      right: '0',
      top: '100%',
      marginTop: '4px',
      background: '#fff',
      border: '1px solid #e1e3e5',
      borderRadius: '10px',
      boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
      padding: '12px 0',
      minWidth: '220px',
      zIndex: 1000,
    },
    sortLabel: {
      padding: '4px 16px 8px',
      fontSize: '13px',
      fontWeight: '600',
      color: '#6d7175',
    },
    sortOption: (active) => ({
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '8px 16px',
      cursor: 'pointer',
      fontSize: '14px',
      color: '#202223',
      background: active ? '#f6f6f7' : 'transparent',
      border: 'none',
      width: '100%',
      textAlign: 'left',
      transition: 'background 0.1s ease',
    }),
    sortRadio: (active) => ({
      width: '18px',
      height: '18px',
      borderRadius: '50%',
      border: active ? '5px solid #2c6ecb' : '2px solid #8c9196',
      background: '#fff',
      flexShrink: 0,
    }),
    sortDivider: {
      height: '1px',
      background: '#e1e3e5',
      margin: '8px 0',
    },
    directionOption: (active) => ({
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '8px 16px',
      cursor: 'pointer',
      fontSize: '14px',
      color: '#202223',
      background: active ? '#f1f8ff' : 'transparent',
      border: 'none',
      width: '100%',
      textAlign: 'left',
      fontWeight: active ? '500' : '400',
      transition: 'background 0.1s ease',
    }),
  }

  const currentSortLabel = sortOptions.find((o) => o.field === sortField)?.label || 'Product title'

  return (
    <>
      <section className='my-5'>
        <div className='container'>
          <Row className='align-items-center mb-4'>
            <Col>
              <h1 className='commonTitle--xl'>Products</h1>
            </Col>
            <Col className='text-end'>
              {selectedProducts.length > 0 ? (
                <Button
                  variant='danger'
                  onClick={bulkDeleteHandler}
                  disabled={loadingBulkDelete}
                >
                  <i className='fas fa-trash me-2'></i>
                  Delete Selected ({selectedProducts.length})
                </Button>
              ) : (
                <a href='/admin/product/create'>
                  <Button className='my-3 btnCommon'>
                    <span>Create Product</span>
                    <i className='fas fa-plus ms-3'></i>
                  </Button>
                </a>
              )}
            </Col>
          </Row>

          {loadingDelete && <Loader />}
          {errorDelete && <Message variant='danger'>{errorDelete}</Message>}
          {loadingBulkDelete && <Loader />}
          {errorBulkDelete && <Message variant='danger'>{errorBulkDelete}</Message>}
          {loadingCreate && <Loader />}
          {errorCreate && <Message variant='danger'>{errorCreate}</Message>}
          {loading ? (
            <Loader />
          ) : error ? (
            <Message variant='danger'>{error}</Message>
          ) : (
            <>
              {/* Shopify-style toolbar */}
              <div style={toolbarStyles.wrapper}>
                {/* Search bar (shown when search is active) */}
                {showSearch && (
                  <div style={toolbarStyles.searchBar}>
                    <div style={toolbarStyles.searchInputWrapper}>
                      <i className='fas fa-search' style={toolbarStyles.searchIcon}></i>
                      <input
                        ref={searchInputRef}
                        type='text'
                        placeholder='Searching all products'
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={toolbarStyles.searchInput}
                      />
                    </div>
                    <button
                      style={toolbarStyles.cancelBtn}
                      onClick={handleSearchCancel}
                    >
                      Cancel
                    </button>
                  </div>
                )}

                {/* Tab bar with status filters and icons */}
                <div style={toolbarStyles.tabBar}>
                  <div style={toolbarStyles.tabsLeft}>
                    <span style={toolbarStyles.statusLabel}>Status</span>
                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      style={toolbarStyles.statusSelect}
                    >
                      <option value='all'>All</option>
                      <option value='active'>Active</option>
                      <option value='draft'>Draft</option>
                    </select>
                  </div>
                  <div style={toolbarStyles.iconsRight}>
                    {!showSearch && (
                      <button
                        style={toolbarStyles.iconBtn}
                        onClick={() => setShowSearch(true)}
                        title='Search'
                      >
                        <i className='fas fa-search'></i>
                      </button>
                    )}
                    <div style={{ position: 'relative' }} ref={sortDropdownRef}>
                      <button
                        style={toolbarStyles.iconBtn}
                        onClick={() => setShowSortDropdown(!showSortDropdown)}
                        title='Sort'
                      >
                        <i className='fas fa-sort-amount-down-alt'></i>
                      </button>
                      {showSortDropdown && (
                        <div style={toolbarStyles.sortDropdown}>
                          <div style={toolbarStyles.sortLabel}>Sort by</div>
                          {sortOptions.map((opt) => (
                            <button
                              key={opt.field}
                              style={toolbarStyles.sortOption(sortField === opt.field)}
                              onClick={() => setSortField(opt.field)}
                              onMouseEnter={(e) => (e.currentTarget.style.background = '#f6f6f7')}
                              onMouseLeave={(e) => (e.currentTarget.style.background = sortField === opt.field ? '#f6f6f7' : 'transparent')}
                            >
                              <div style={toolbarStyles.sortRadio(sortField === opt.field)} />
                              {opt.label}
                            </button>
                          ))}
                          <div style={toolbarStyles.sortDivider} />
                          <button
                            style={toolbarStyles.directionOption(sortDirection === 'asc')}
                            onClick={() => setSortDirection('asc')}
                            onMouseEnter={(e) => (e.currentTarget.style.background = '#f1f8ff')}
                            onMouseLeave={(e) => (e.currentTarget.style.background = sortDirection === 'asc' ? '#f1f8ff' : 'transparent')}
                          >
                            <i className='fas fa-arrow-up' style={{ fontSize: '12px' }}></i>
                            A-Z
                          </button>
                          <button
                            style={toolbarStyles.directionOption(sortDirection === 'desc')}
                            onClick={() => setSortDirection('desc')}
                            onMouseEnter={(e) => (e.currentTarget.style.background = '#f1f8ff')}
                            onMouseLeave={(e) => (e.currentTarget.style.background = sortDirection === 'desc' ? '#f1f8ff' : 'transparent')}
                          >
                            <i className='fas fa-arrow-down' style={{ fontSize: '12px' }}></i>
                            Z-A
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Table */}
                <Table striped hover responsive className='table-sm productList__table mb-0'>
                  <thead>
                    <tr>
                      <th style={{ width: '40px' }}>
                        <Form.Check
                          type='checkbox'
                          checked={allSelected}
                          onChange={toggleSelectAll}
                        />
                      </th>
                      <th>S.N</th>
                      <th>
                        {currentSortLabel} {sortDirection === 'asc' ? <i className='fas fa-arrow-down' style={{ fontSize: '11px', opacity: 0.5 }}></i> : <i className='fas fa-arrow-up' style={{ fontSize: '11px', opacity: 0.5 }}></i>}
                      </th>
                      <th>STATUS</th>
                      <th>QTY</th>
                      <th>PRICE</th>
                      <th>BRAND</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedProducts.map((product, i) => (
                      <tr key={product._id} onClick={() => toggleSelectProduct(product._id)} style={{ cursor: 'pointer' }}>
                        <td className='text-center'>
                          <Form.Check
                            type='checkbox'
                            checked={selectedProducts.includes(product._id)}
                            onChange={() => toggleSelectProduct(product._id)}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </td>
                        <td className='text-center'>{serialOffset + i + 1}</td>
                        <td>{product.name}</td>
                        <td>
                          <span style={{
                            padding: '2px 8px',
                            borderRadius: '10px',
                            fontSize: '12px',
                            fontWeight: '500',
                            background: product.isActive ? '#aee9d1' : '#fed3d1',
                            color: product.isActive ? '#1a5c3a' : '#6e1717',
                          }}>
                            {product.isActive == true ? 'Active' : 'Draft'}
                          </span>
                        </td>
                        <td>{product.quantity}</td>
                        <td>₹{product.price}</td>
                        <td>{product.brand}</td>
                        <td className='edit-btns' onClick={(e) => e.stopPropagation()}>
                          <Button
                            style={{ marginLeft: '12px' }}
                            variant='light'
                            className='btn-sm action__Btn'
                            onClick={() => duplicateHandler(product._id)}
                          >
                            <i className='fas fa-clone'></i>
                          </Button>

                          <LinkContainer to={`/admin/product/${product._id}/edit`}>
                            <Button variant='light' className='btn-sm action__Btn'>
                              <i className='fas fa-edit'></i>
                            </Button>
                          </LinkContainer>

                          <Button
                            variant='danger'
                            className='btn-sm'
                            onClick={() => deleteHandler(product._id)}
                          >
                            <i className='fas fa-trash'></i>
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
              <div className='mt-3'>
                <Paginate pages={pages} page={page} isAdmin={true} keyword={keyword} />
              </div>
            </>
          )}
        </div>
      </section>
    </>
  )
}

export default ProductListScreen
